<template>
    <section class="indexpage-container">
        <div class="indexpage-main-container" v-bind:class="{'indexpage-main-container--testnet': IS_TESTNET}">
            <div class="indexpage-main">
                <h1>
                    <span class="title-main">{{$t('indexpage.explore')}} </span>
                    <span class="title">The Open Network</span>
                </h1>
            </div>

            <app-search class="indexpage-search"
                focus-on-load
                show-search-button
                v-bind:placeholder="$t('indexpage.search_placeholder')"/>
        </div>

        <template v-if="!IS_TESTNET && isMobile">
            <mobile-number-blocks />
        </template>

        <div class="indexpage-widget" v-if="!IS_TESTNET && !isMobile">
            <div class="indexpage-widget-col">
                <market-data />
                <div class="index-slot">
                    <component v-bind:is="adBanner"/>
                </div>
                <top-jettons />
            </div>
            <div class="indexpage-widget-col">
                <trending-apps />
                <rich-wallets />
            </div>
            <div class="indexpage-widget-col">
                <news-feed />
            </div>
        </div>
        <section class="indexpage-mobile-container" v-if="!IS_TESTNET && isMobile">
            <div class="index-slot index-slot--mobile">
                <component v-bind:is="adBanner"/>
            </div>
            <mobile-switcher-block />
        </section>
        <app-footer/>
    </section>
</template>

<script>
import AppSearch from '~/components/search/AppSearch.vue';
import MarketData from '~/components/widgets/MarketData/MarketData.vue';
import NewsFeed from '~/components/widgets/NewsFeed/NewsFeed.vue';
import TrendingApps from '~/components/widgets/TrendingApps/TrendingApps.vue';
import RichWallets from '~/components/widgets/RichWallets/RichWallets.vue';
import TopJettons from '~/components/widgets/TopJettons/TopJettons.vue';
import StakeeBanner from '~/components/widgets/Stakee/StakeeBlock.vue';
import StakeeMobileBanner from '~/components/widgets/Stakee/StakeeMobileBlock.vue';
import MobileNumberBlocks from '~/components/widgets/Mobile/NumberBlocks.vue';
import MobileSwitcherBlock from '~/components/widgets/Mobile/SwitcherBlock.vue';
import AppFooter from '~/components/AppFooter.vue';
import { isRussianSpeaker } from '~/components/widgets/Ads/audience.js';
import { IS_TESTNET } from '~/config';

// The Gram VPN banners carry the lottie player and its animation data,
// so they come in their own async chunks:
const GramVpnBanner = () => import(/* webpackChunkName: "ad-block" */ '~/components/widgets/Ads/DefaultAdBlock.vue');
const GramVpnMobileBanner = () => import(/* webpackChunkName: "ad-block-mobile" */ '~/components/widgets/Ads/DefaultAdBlockMobile.vue');

export default {
    computed: {
        IS_TESTNET() {
            return IS_TESTNET;
        },
        // One banner per audience, nothing swaps in front of the visitor:
        adBanner() {
            const [stakee, gramVpn] = this.isMobile
                ? [StakeeMobileBanner, GramVpnMobileBanner]
                : [StakeeBanner, GramVpnBanner];

            return isRussianSpeaker(this.$i18n.locale) ? gramVpn : stakee;
        },
    },
    metaInfo() {
        return {
            title: this.$t('indexpage.meta.title'),
            meta: [{
                vmid: 'description', name: 'description',
                content: this.$t('indexpage.meta.description'),
            }],

            // Organization entity for search engines and AI assistants, so that
            // "Tonscan" resolves to tonscan.org and not to the lookalike domains:
            script: [{
                vmid: 'ld-organization',
                type: 'application/ld+json',
                json: {
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: 'Tonscan',
                    alternateName: 'tonscan.org',
                    url: 'https://tonscan.org/',
                    logo: 'https://tonscan.org/favicon.svg',
                    description: 'Tonscan (tonscan.org) is an open source blockchain explorer for TON, The Open Network: addresses, transactions, jettons, NFT, apps and network statistics.',
                    sameAs: [
                        'https://github.com/catchain/tonscan',
                        'https://t.me/catchain',
                        'https://docs.ton.org/ecosystem/explorers/overview',
                        'https://ton.app/explorers',
                    ],
                },
            }],
        };
    },

    components: {
        AppSearch,
        MarketData,
        NewsFeed,
        TrendingApps,
        RichWallets,
        TopJettons,
        MobileNumberBlocks,
        MobileSwitcherBlock,
        AppFooter,
    },
};
</script>

<style lang="scss">
.indexpage-container {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
    position: relative;
    min-height: calc(100vh - 165px);
}

.indexpage-main-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.indexpage-main-container--testnet {
    flex: 1;
}

.indexpage-mobile-container {
    display: flex;
    width: calc(100% + 16px);
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
    position: relative;
}

.indexpage-main h1 {
    font-family: var(--font-heading);
    font-size: 44px;
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.1;
    margin-bottom: 36px;
}

.indexpage-main p {
    color: var(--body-muted-text-color);
    font-size: 16px;
    line-height: 1.7em;
    max-width: 480px;
    margin: 0 auto 30px;
}

.indexpage-main {
    text-align: center;
}

.indexpage-settings-button {
    position: absolute;
    top: -45px;
    right: 8px;
    opacity: .8;
    cursor: pointer;
    stroke: var(--body-muted-text-color);
    width: 18px;
    height: 18px;
    &:hover {
        opacity: 1;
    }
}

.indexpage-logo {
    display: block;
    width: 180px;
    height: 180px;
    margin: 0 auto 24px;
}

.indexpage-widget {
    width: 100%;
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.indexpage-widget-col > .widget:last-child {
    margin-bottom: 0;
}

.indexpage-widget-col {
    width: calc(33.33333% - 14px);
}

.btn {
    width: 100%;
    padding: 10px;
    text-align: center;
    background: var(--button-widget);
    border-radius: 5px;
    cursor: pointer;
    color: var(--button-widget-text);
}

.btn:hover {
    background: var(--button-widget-hover);
}

.title {
    -webkit-text-fill-color: transparent;
    -webkit-font-feature-settings: "clig" off,"liga" off;
    font-feature-settings: "clig" off,"liga" off;
    background: linear-gradient(89deg,#2b82eb,#1cc8ff);
    background-clip: text;
    -webkit-background-clip: text;
    position: relative;
}

.btn {
    -webkit-transition: all .4s;
    -o-transition: all .4s;
    transition: all .4s;
}

.widget-item {
    padding: 10px;
}

// The Gram VPN banner arrives in its own chunk; the slot is already as tall
// as the banner (plus its margin), so nothing below moves when it lands.
// Not called "ad-rotator": EasyList hides `.ad-rotator`.
.index-slot {
    display: grid;
    width: 100%; // the mobile container centres its children, it doesn't stretch them
    min-height: 220px;

    &--mobile {
        min-height: 81px;
    }
}

.widget {
    background-color: var(--card-background);
    border: 1px solid var(--card-border-color);
    border-radius: 16px;
    box-shadow: 0 1px 2px var(--card-box-shadow-color), 0 12px 32px -18px var(--card-box-shadow-color);
    font-size: 14px;
    margin-bottom: 20px;
    min-width: 0;
    overflow: hidden;
    .widget-header {
        display: flex;
        justify-content: space-between;
        padding: 20px 20px 0 20px;
        grid-row: 1 / -1;
        grid-column: 1;
        min-width: 0;
    }
    .widget-content {
        grid-row: 1 / -1;
        grid-column: 1;
        min-width: 0;
    }
    .widget-title {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .06em;
        text-transform: uppercase;
        color: var(--card-header-tab-color);
    }
    .widget-link {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .06em;
        text-transform: uppercase;
    }
    .widget-link a:hover {
        text-decoration: none;
        color: var(--widget-view-all-btn-hover);
    }
    .widget-type {
        color: var(--body-muted-text-color);
        margin-bottom: 9px;
    }
    .widget-price {
        font-size: 23px;
    }
}

.widget-wide {
    grid-column: span 2;
}

.indexpage-search {
    max-width: 600px;
    font-size: 16px;
    margin-bottom: 70px;
    .search-input {
        border-radius: 14px;
        border: 2px solid var(--card-border-color);
        box-shadow: 0 1px 2px var(--card-box-shadow-color), 0 16px 40px -16px var(--card-box-shadow-color);
        background: var(--indexpage-search-background-color);
        &__input {
            padding: 12px 24px;
            background: transparent;
        }
    }

    .search-results {
        padding-bottom: 6px;
        &__item {
            margin-bottom: -2px;
            padding: 0 4px;
            &:first-child {
                margin-top: 4px;
            }
            &:last-child {
               margin-bottom: 0;
            }
        }
        &__link {
            border-radius: 8px;
        }
    }

    .search-result {
        &__left {
            margin-left: -4px;
        }
    }
    .search-input__input {
        padding: 12px 26px;
    }
}

@media screen and (max-width: 1170px) {
    .indexpage-widget-col {
        width: calc(100%);

        .widget {
            width: 100%;
            max-width: 600px;
            margin-left: calc((100% - 600px) / 2);
        }
    }
}

@media screen and (max-width: 768px) {
    .indexpage-search {
        margin-bottom: 30px;
    }
    .widget-column {
        width: 50%;
    }
    .indexpage-main h1 {
        font-size: 34px;
        margin: 0 0 30px 0;
    }
}

@media screen and (max-width: 600px) {
    .widget {
        width: 100%;
    }
    .title-main {
        display: block;
    }
    .indexpage-search {
        margin-bottom: 20px;
        .search-input__input {
            padding-left: 18px;
            padding-right: 18px;
        }

        .search-results {
            &__link {
                margin-left: 0;
                margin-right: 0;
            }
        }
    }
}
</style>
